import { useApiStore } from "@src/stores/ApiStore";
import { AlertTypes, useWidgetStore } from "@src/stores/WidgetStore";
import _omitBy from "lodash/omitBy";
import _isUndefined from "lodash/isUndefined";
import {
  QueryKey,
  QueryObserverOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/vue-query";
import { computed, inject, ref } from "vue";
import _map from "lodash/map";

export interface FetchError {
  status?: number;
  message?: string;
  data?: Record<string, any>;
}

interface useFetchParams {
  url: string;
  init: RequestInit;
}

interface useApiGetParams {
  url: URL;
  init?: RequestInit;
}

interface useApiPostParams {
  url: string;
  data: Record<string, any>;
  init?: RequestInit;
}

export interface IApiFilter {
  (url: URL): URL;
}

export interface IApiListParams {
  limit: number;
  filters?: IApiFilter[];
  sort?: [direction: ApiSortDirection, property: string];
}

export interface IUseApiQueryParams<TData> {
  queryUrl: URL;
  queryKey: QueryKey;
  queryMapper?: (item: any) => TData;
}

export interface IUseApiListParams<TData> {
  listKey: QueryKey;
  listParams: IApiListParams;
  listMethod: (
    params: IApiListParams & { offset: number },
    signal?: AbortSignal
  ) => Promise<{
    data: TData[];
    limit: number;
    offset: number;
    total: number;
  }>;
}

export enum FetchMethods {
  GET = "GET",
  DELETE = "DELETE",
  POST = "POST",
  PUT = "PUT",
}

export enum ApiSortDirection {
  ASC = "",
  DESC = "-",
}

const { addToast } = useWidgetStore();
const { doFetch } = useApiStore();

/**
 * @name useApiUrl
 * @desc Construct API url for given path.
 */

export function useApiUrl(path: string, filters: IApiFilter[] = []) {
  // Construct URL
  const url = new URL([import.meta.env.VITE_API_URL, "api", path].join("/"));
  // Apply filter parameters
  filters.reduce((_url, filter) => filter(_url), url);
  // Return URL
  return url;
}

/**
 * @name useFetch
 * @desc A wrapper function around the native Fetch API
 * (https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API),
 * with basic response handling and error mapping.
 */

export async function useFetch(
  { url, init }: useFetchParams,
  useCache = false
) {
  try {
    return doFetch(url, init, useCache);
  } catch (e) {
    const error = e as TypeError;
    throw { name: error.name, message: error.message } as FetchError;
  }
}

export function useApi() {
  const locale = ref(inject("locale", ""));

  /**
   * @name useApiGet
   * @desc A 'GET' wrapper function around useFetch
   */

  function useApiGet({ url, init }: useApiGetParams, useCache = true) {
    url?.searchParams?.set("lang", locale.value);
    return useFetch(
      {
        url: url.toString(),
        init: {
          ...init,
          method: FetchMethods.GET,
          headers: { "Content-Type": "application/json" },
        },
      },
      useCache
    );
  }

  async function useApiList<TData>(
    {
      url,
      limit = 10,
      offset = 0,
      sort,
    }: IApiListParams & { url: URL; offset: number },
    signal?: AbortSignal,
    mapperFn: (item: any) => TData = (item) => item as TData
  ) {
    // Set 'order' (sort) parameter
    if (sort?.length) url.searchParams.set("order", sort.join(""));
    // Set 'limit' parameter
    if (Number.isInteger(limit)) url.searchParams.set("limit", `${limit}`);
    // Set 'offset' parameter
    if (Number.isInteger(offset)) url.searchParams.set("offset", `${offset}`);
    // Fetch data
    const { data: response } = await useApiGet(
      { url, init: { signal } },
      false
    );
    // Map data
    const mappedData = _map(response.data, mapperFn) as TData[];
    // Return data and totals
    return {
      data: mappedData,
      limit,
      offset,
      sort,
      total: response.total,
    };
  }

  function useApiQuery<TData>(
    {
      queryUrl,
      queryKey,
      queryMapper = (item) => item,
    }: IUseApiQueryParams<TData>,
    options: Partial<QueryObserverOptions>
  ) {
    return useQuery<TData>({
      queryKey: [...queryKey, `lang=${locale.value}`],
      queryFn: async ({ signal }) => {
        const { data: response } = await useApiGet(
          { url: queryUrl, init: { signal } },
          false
        );
        return queryMapper(response.data);
      },
      placeholderData: ref({}),
      retry: false,
      gcTime: 5000 * 60, // 5 minutes
      throwOnError: (e) => {
        useErrorHandler(e);
        return false;
      },
      ...(options as any),
    });
  }

  function useApiInfiniteList<TData>({
    listKey,
    listMethod,
    listParams,
  }: IUseApiListParams<TData>) {
    const { limit, sort } = listParams;

    return useInfiniteQuery({
      queryKey: [
        ...listKey,
        `sort=${sort?.join("")}`,
        `limit=${limit}`,
        `lang=${locale.value}`,
      ],
      queryFn: async ({ pageParam, signal }) => {
        const { data, limit, offset, total } = await listMethod(
          { ...listParams, offset: pageParam },
          signal
        );
        // Determine next offset
        const nextOffset =
          !limit || offset + limit >= total ? undefined : offset + limit;
        // Return next offset and page data
        return { nextOffset, pageData: data };
      },
      getNextPageParam: (lastPage) => lastPage?.nextOffset,
      initialPageParam: 0,
      retry: false,
      gcTime: 1000 * 60, // 60 seconds,
      throwOnError: (e) => {
        useErrorHandler(e);
        return false;
      },
    });
  }

  function useApiPaginatedList<TData>({
    listKey,
    listMethod,
    listParams,
  }: IUseApiListParams<TData>) {
    const itemTotal = ref(0);
    const pageIndex = ref(1);
    const { limit, sort } = listParams;

    const queryResult = useQuery({
      queryKey: [
        ...listKey,
        `sort=${sort?.join("")}`,
        `limit=${limit}`,
        `lang=${locale.value}`,
        { pageIndex }, // Important for pagination to work
      ],
      queryFn: async ({ signal }) => {
        const offset = (pageIndex.value - 1) * limit;
        const { data, total } = await listMethod(
          { ...listParams, offset },
          signal
        );
        itemTotal.value = total;
        return data;
      },
      retry: false,
      gcTime: 1000 * 60, // 60 seconds,
      throwOnError: (e) => {
        useErrorHandler(e);
        return false;
      },
    });

    const pageTotal = computed(() => {
      if (!limit) return 1; // Can only be 1 page if limit=0
      return Math.max(Math.ceil(itemTotal.value / limit), 1);
    });

    const itemFrom = computed(() => {
      if (!itemTotal.value) return 0;
      return limit * (pageIndex.value - 1) + 1;
    });

    const itemTo = computed(() => {
      if (!limit) return itemTotal.value;
      return Math.min(limit * pageIndex.value, itemTotal.value);
    });

    const hasPrevPage = computed(() => {
      return pageIndex.value > 1;
    });

    const hasNextPage = computed(() => {
      return pageIndex.value < pageTotal.value;
    });

    const prevPage = () => {
      pageIndex.value = Math.max(pageIndex.value - 1, 1);
    };

    const nextPage = () => {
      if (!queryResult.isPlaceholderData.value) {
        pageIndex.value = pageIndex.value + 1;
      }
    };

    return {
      ...queryResult,
      // Custom data
      hasNextPage,
      hasPrevPage,
      itemFrom,
      itemTo,
      itemTotal,
      pageIndex,
      pageSize: limit,
      pageTotal,
      // Custom methods
      nextPage,
      prevPage,
    };
  }

  return {
    useApiGet,
    useApiList,
    useApiQuery,
    useApiInfiniteList,
    useApiPaginatedList,
  };
}

/**
 * @name useApiPost
 * @desc A 'POST' wrapper function around useFetch
 */

export function useApiPost({ url, data, init }: useApiPostParams) {
  return useFetch({
    url,
    init: {
      ...init,
      method: FetchMethods.POST,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  });
}

export function getQueryParts(url: URL) {
  return decodeURIComponent(url.search.replace(/^\?/, "")).split("&");
}

/**
 * @name useErrorHandler
 * @desc Provides default handling when a FetchError is encountered.
 */

export function useErrorHandler(e: any, hook?: (e: FetchError) => void) {
  const error = e as FetchError;
  // If fetch was aborted, skip handling
  if (e.name === "AbortError") return;
  addToast({
    message:
      error.data?.error?.message ||
      error.message ||
      "Oops, something went wrong!",
    type: AlertTypes.DANGER,
  });
  // Execute hook function, if passed
  if (typeof hook === "function") hook(error);
}
